// frontend/src/components/SmsMessageModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-11-24
// Updated:      2026-09-16
//
// Description:
//   SMS-style messenger modal for a Job.
//   • Portalled to document.body with fixed inset-0 overlay (no clipping under JobEditModal).
//   • Threading fix (2025-12-22):
//       - Resolve and reuse latest conversation_id for (tenant, contact, channel='sms')
//         preferring job_id match, to prevent conversation splitting.
//       - Load messages primarily from /api/conversations/{conversation_id}/messages.
//
//   Patch (2025-12-22):
//     ✅ Fix empty thread rendering when backend message rows omit "channel".
//     ✅ More robust message normalisation (body_text/body/message/etc).
//     ✅ Direction inference when missing.
//
// NOTE:
//   This is a full-file replacement. No functionality removed.
//
// Responsive pass (2026-09-16):
//   • Mobile uses a full-height messenger sheet with desktop sizing preserved at sm+.
//   • Chat body uses min-height:0 so it owns scrolling instead of forcing modal overflow.
//   • Header/contact metadata and destination number wrap safely on narrow screens.
//   • Message bubbles are wider on phones and retain the existing desktop width at sm+.
//   • Composer stacks cleanly on mobile; Send remains compact on larger screens.
//   • No threading, conversation resolution, message normalisation, E.164 or send logic changed.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/axiosInstance";

// ---------------------------------------------------------------------------
// Simple UK E.164 normaliser
// ---------------------------------------------------------------------------
const normaliseToE164Uk = (raw) => {
  if (!raw) return "";
  let t = String(raw).trim();
  if (!t) return "";
  t = t.replace(/[\s\-()]/g, "");
  if (t.startsWith("+")) return t;
  const digits = t.replace(/\D/g, "");
  if (t.startsWith("0") && digits.length >= 10 && digits.length <= 12) {
    return `+44${digits.slice(1)}`;
  }
  return t;
};

const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const safeArr = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
};

const SmsMessageModal = ({
  open,
  onClose,
  jobId,
  jobRef,
  job,
  contactId,
  contactName,
  contactPhone,
  contactAvatarUrl,
  defaultTo = "",
}) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState("");

  const [jobMeta, setJobMeta] = useState(null);

  const [resolvedContact, setResolvedContact] = useState(null);

  const [toOverride, setToOverride] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [isSending, setIsSending] = useState(false);

  // ---------------------------------------------------------------------------
  // Effective contact_id
  // ---------------------------------------------------------------------------
  const effectiveContactId = useMemo(() => {
    if (contactId) return contactId;
    if (job && job.contact_id) return job.contact_id;
    if (jobMeta && jobMeta.contact_id) return jobMeta.contact_id;
    return null;
  }, [contactId, job, jobMeta]);

  // ---------------------------------------------------------------------------
  // Load job
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;

    api
      .get(`/api/jobs/${jobId}`)
      .then((res) => {
        if (cancelled) return;
        setJobMeta(res.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setJobMeta(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  // ---------------------------------------------------------------------------
  // Load contact
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (!effectiveContactId) {
      setResolvedContact(null);
      return;
    }

    let cancelled = false;

    api
      .get(`/api/contacts/${effectiveContactId}`)
      .then((res) => {
        if (cancelled) return;
        setResolvedContact(res.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedContact(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, effectiveContactId]);

  // ---------------------------------------------------------------------------
  // Extract contact name
  // ---------------------------------------------------------------------------
  const apiContactName = useMemo(() => {
    if (!resolvedContact) return "";
    const rc = resolvedContact;

    if (typeof rc.display_name === "string" && rc.display_name.trim()) return rc.display_name.trim();
    if (typeof rc.full_name === "string" && rc.full_name.trim()) return rc.full_name.trim();

    const first = rc.first_name?.trim?.() || "";
    const last = rc.last_name?.trim?.() || "";
    if (first || last) return `${first} ${last}`.trim();

    if (typeof rc.name === "string" && rc.name.trim()) return rc.name.trim();

    return "";
  }, [resolvedContact]);

  // ---------------------------------------------------------------------------
  // Extract contact phone
  // ---------------------------------------------------------------------------
  const apiContactPhone = useMemo(() => {
    if (!resolvedContact) return "";
    const rc = resolvedContact;

    const primary = [
      rc.mobile,
      rc.mobile_phone,
      rc.phone_mobile,
      rc.phone,
      rc.phone_number,
      rc.telephone,
      rc.mobile_number,
      rc.cell,
      rc.cell_phone,
    ];

    for (const c of primary) {
      if (typeof c === "string" && c.trim()) return c.trim();
    }

    return "";
  }, [resolvedContact]);

  const fallbackPhone = useMemo(() => contactPhone || defaultTo || "", [contactPhone, defaultTo]);

  const effectiveToPhone = useMemo(() => {
    const p = apiContactPhone || fallbackPhone;
    return (p || "").trim();
  }, [apiContactPhone, fallbackPhone]);

  useEffect(() => {
    if (open) setToOverride(effectiveToPhone);
  }, [open, effectiveToPhone]);

  // ---------------------------------------------------------------------------
  // Header label
  // ---------------------------------------------------------------------------
  const headerPrimaryText = useMemo(() => {
    if (apiContactName) return apiContactName;
    if (contactName?.trim()) return contactName.trim();
    if (apiContactPhone) return apiContactPhone;
    if (fallbackPhone.trim()) return fallbackPhone.trim();
    return "Unknown contact";
  }, [apiContactName, apiContactPhone, contactName, fallbackPhone]);

  const headerSecondaryText = useMemo(() => {
    const phone = apiContactPhone || fallbackPhone.trim() || "";
    if (!phone) return "";
    if (headerPrimaryText !== phone) return phone;
    return "";
  }, [apiContactPhone, fallbackPhone, headerPrimaryText]);

  const avatarUrl = useMemo(() => {
    if (contactAvatarUrl) return contactAvatarUrl;
    if (resolvedContact?.avatar_url) return resolvedContact.avatar_url;
    return null;
  }, [contactAvatarUrl, resolvedContact]);

  const initials = useMemo(() => {
    if (avatarUrl) return "";

    const basisName = apiContactName || contactName || "";
    const basisPhone = apiContactPhone || fallbackPhone || "";

    if (basisName.trim()) {
      const parts = basisName.split(" ").filter(Boolean).slice(0, 2);
      return parts.map((p) => p[0].toUpperCase()).join("");
    }

    if (basisPhone.trim()) {
      const digits = basisPhone.replace(/\D/g, "");
      if (digits.length >= 2) return digits.slice(-2);
    }

    return "UC";
  }, [avatarUrl, apiContactName, contactName, apiContactPhone, fallbackPhone]);

  // ---------------------------------------------------------------------------
  // Effective JobRef/Id
  // ---------------------------------------------------------------------------
  const effectiveJobRef = useMemo(() => jobRef || job?.job_ref || jobMeta?.job_ref || "", [jobRef, job, jobMeta]);

  const effectiveJobId = useMemo(() => jobId || job?.id || jobMeta?.id || null, [jobId, job, jobMeta]);

  const jobLabel = useMemo(() => {
    if (effectiveJobRef && effectiveJobId) return `Job Ref: ${effectiveJobRef} · Job ID: ${effectiveJobId}`;
    if (effectiveJobRef) return `Job Ref: ${effectiveJobRef}`;
    if (effectiveJobId) return `Job ID: ${effectiveJobId}`;
    return "";
  }, [effectiveJobRef, effectiveJobId]);

  // ---------------------------------------------------------------------------
  // Effective customer_id for persistence
  // ---------------------------------------------------------------------------
  const effectiveCustomerId = useMemo(() => {
    if (resolvedContact) {
      const rc = resolvedContact;
      if (rc.customer_id != null) return rc.customer_id;
      if (rc.customerId != null) return rc.customerId;
    }
    if (jobMeta) {
      const jm = jobMeta;
      if (jm.customer_id != null) return jm.customer_id;
      if (jm.client_id != null) return jm.client_id;
    }
    if (job) {
      const j = job;
      if (j.customer_id != null) return j.customer_id;
      if (j.client_id != null) return j.client_id;
    }
    return null;
  }, [resolvedContact, jobMeta, job]);

  // ---------------------------------------------------------------------------
  // Subject base for SMS conversation
  // ---------------------------------------------------------------------------
  const smsSubjectBase = useMemo(() => {
    const parts = [];
    if (effectiveJobRef) parts.push(`Job ${effectiveJobRef}`);
    if (headerPrimaryText) parts.push(headerPrimaryText);
    const joined = parts.join(" – ");
    return joined || "SMS message";
  }, [effectiveJobRef, headerPrimaryText]);

  // ---------------------------------------------------------------------------
  // Resolve latest conversation_id for sms
  // ---------------------------------------------------------------------------
  const resolveLatestConversationId = useCallback(async () => {
    if (!effectiveContactId) return null;

    try {
      const { data } = await api.get("/api/conversations", {
        params: {
          limit: 200,
          offset: 0,
          job_id: effectiveJobId ?? undefined,
        },
      });

      const rows = safeArr(data);
      const channel = "sms";

      const byContactChannel = rows.filter(
        (c) =>
          Number(c?.contact_id) === Number(effectiveContactId) &&
          String(c?.channel || "").toLowerCase() === channel
      );

      if (!byContactChannel.length) return null;

      const jobMatched = (effectiveJobId
        ? byContactChannel.filter((c) => Number(c?.job_id) === Number(effectiveJobId))
        : []) || [];

      const pickFrom = jobMatched.length ? jobMatched : byContactChannel;
      const sorted = [...pickFrom].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
      return sorted[0]?.id ?? null;
    } catch {
      return null;
    }
  }, [effectiveContactId, effectiveJobId]);

  // On open: lock to latest conversation thread
  useEffect(() => {
    if (!open) return;
    if (!effectiveContactId) return;

    let cancelled = false;
    const run = async () => {
      const latest = await resolveLatestConversationId();
      if (cancelled) return;
      if (latest) setConversationId(latest);
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [open, effectiveContactId, resolveLatestConversationId]);

  // ---------------------------------------------------------------------------
  // Normalise messages (robust)
  // ---------------------------------------------------------------------------
  const normaliseMessages = (rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];

    return safeRows
      .map((m) => {
        const created_at = m.created_at || m.sent_at || m.received_at || m.timestamp || null;

        const directionKey =
          m && typeof m === "object"
            ? Object.keys(m).find((k) => k.toLowerCase().includes("direction"))
            : null;

        const rawDirection =
          m.direction ??
          m.message_direction ??
          m.messageDirection ??
          (directionKey ? m[directionKey] : null);

        // Infer if missing: inbound if it has provider inbound flags, else outbound
        const direction =
          typeof rawDirection === "string" && rawDirection.trim()
            ? rawDirection.toLowerCase()
            : m.inbound === true
            ? "inbound"
            : "outbound";

        const bodyCandidate =
          m.body_text ||
          m.text_body ||
          m.bodyText ||
          m.body ||
          m.message ||
          m.text ||
          (m.body_html ? stripHtml(m.body_html) : "") ||
          (m.html_body ? stripHtml(m.html_body) : "") ||
          "";

        const body = String(bodyCandidate || "").trim();

        return {
          id: m.id || m.message_id || `${created_at || "no-ts"}-${Math.random()}`,
          conversation_id: m.conversation_id || m.conversationId || null,
          direction,
          body,
          created_at,
          // channel may not exist; keep it but never rely on it to filter
          channel: m.channel ? String(m.channel).toLowerCase() : undefined,
        };
      })
      .filter((m) => m.body) // ✅ prevent blank bubbles
      .sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return da - db; // oldest -> newest for chat layout
      });
  };

  // ---------------------------------------------------------------------------
  // Load messages:
  // prefer conversation thread endpoint to guarantee true threading
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoadingMessages(true);
    setLoadError(null);

    const load = async () => {
      try {
        let convId = conversationId;

        if (!convId && effectiveContactId) {
          convId = await resolveLatestConversationId();
          if (convId && !cancelled) setConversationId(convId);
        }

        if (convId) {
          const res = await api.get(`/api/conversations/${convId}/messages`);
          if (cancelled) return;

          const arr = safeArr(res.data);

          // ✅ DO NOT filter by channel when backend may omit it
          const normalised = normaliseMessages(arr);

          // eslint-disable-next-line no-console
          console.info("[SmsMessageModal] loaded messages for conv", convId, "→", normalised.slice(0, 10));

          setMessages(normalised);
          return;
        }

        // fallback: job flattened endpoint (older behaviour)
        if (jobId) {
          const res = await api.get(`/api/conversations/job/${jobId}/messages`, {
            params: { channel: "sms" },
          });
          if (!cancelled) setMessages(normaliseMessages(safeArr(res.data)));
        } else {
          setMessages([]);
        }
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[SmsMessageModal] failed to load messages", err?.response?.status, err?.response?.data || err);
        setLoadError("Failed to load SMS history.");
        setMessages([]);
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, jobId, conversationId, effectiveContactId, resolveLatestConversationId]);

  // ---------------------------------------------------------------------------
  // ESC to close
  // ---------------------------------------------------------------------------
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // ---------------------------------------------------------------------------
  // SEND
  // ---------------------------------------------------------------------------
  const handleSend = async () => {
    const text = draft.trim();
    if (!text || isSending) return;

    const targetRaw = (toOverride || effectiveToPhone || "").trim();
    const targetNormalised = normaliseToE164Uk(targetRaw);

    if (!targetNormalised) {
      setLoadError("No mobile number found. Please enter a valid destination number.");
      return;
    }

    setIsSending(true);
    try {
      let convId = conversationId;

      // Try resolve latest conversation first (threading)
      if (!convId) {
        convId = await resolveLatestConversationId();
        if (convId) setConversationId(convId);
      }

      // Create if still none
      if (!convId) {
        const convPayload = {
          job_id: effectiveJobId ?? null,
          customer_id: effectiveCustomerId,
          contact_id: effectiveContactId ?? null,
          subject: smsSubjectBase,
          channel: "sms",
        };

        const res = await api.post("/api/conversations", convPayload);
        const newId = res?.data?.id ?? null;
        if (newId) {
          convId = newId;
          setConversationId(newId);
        }
      }

      if (convId != null) {
        const apiPayload = {
          conversation_id: convId,
          customer_id: effectiveCustomerId,
          channel: "sms",
          subject: smsSubjectBase,
          body_text: text,
          body_html: null,
          metadata_json: {
            to_phone_ui: targetRaw,
            to_phone_normalised: targetNormalised,
          },
          to: targetNormalised,
        };

        await api.post(`/api/conversations/${convId}/send`, apiPayload);

        // refresh the thread from the conversation endpoint (true thread)
        try {
          const res = await api.get(`/api/conversations/${convId}/messages`);
          const arr = safeArr(res.data);

          // ✅ no channel filter
          setMessages(normaliseMessages(arr));
        } catch {
          // fallback: optimistic append
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              conversation_id: convId,
              direction: "outbound",
              body: text,
              created_at: new Date().toISOString(),
              channel: "sms",
            },
          ]);
        }
      }

      setDraft("");
      setLoadError(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[SmsMessageModal] send failed", err?.response?.status, err?.response?.data || err);
      setLoadError("Failed to send SMS – check backend logs.");
    } finally {
      setIsSending(false);
    }
  };

  if (!open) return null;

  const remaining = 160 - (draft?.length || 0);

  return createPortal(
    <div
      className="fixed inset-0 z-[150000] flex items-center justify-center bg-black/60 p-0 sm:p-4"
      onMouseDown={onClose}
    >
      <div
        className="flex h-[100dvh] w-full min-w-0 max-w-2xl flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:h-auto sm:max-h-[80vh] sm:rounded-3xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="relative flex min-w-0 shrink-0 items-center border-b border-gray-200 bg-white px-3 py-3 pr-12 sm:px-5 sm:py-4 sm:pr-14">
          <div className="mr-3 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#4A90E2] text-sm font-semibold text-white">
            {avatarUrl ? (
              <img src={avatarUrl} alt={headerPrimaryText} className="w-full h-full object-cover" />
            ) : (
              <span>{initials}</span>
            )}
          </div>

          <div className="min-w-0 flex-1 flex flex-col">
            <span className="break-words font-semibold leading-tight text-gray-900">{headerPrimaryText}</span>

            {headerSecondaryText && <span className="mt-0.5 break-all text-[11px] text-gray-600">{headerSecondaryText}</span>}

            {jobLabel && <span className="mt-0.5 break-words text-[11px] text-gray-500">{jobLabel}</span>}

            {conversationId && (
              <span className="mt-0.5 break-words text-[11px] text-gray-400">
                Conv: <span className="font-mono">{conversationId}</span>
              </span>
            )}
          </div>

          <div className="ml-2 hidden shrink-0 text-[11px] font-semibold tracking-widest text-gray-500 sm:block">SMS</div>

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/10 text-base leading-none text-gray-800 hover:bg-black/15 active:bg-black/25 sm:right-4 sm:top-4 sm:h-7 sm:w-7"
          >
            ×
          </button>
        </div>

        {/* TO LINE */}
        <div className="flex min-w-0 shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-3 py-2 text-[11px] text-gray-700 sm:px-5">
          <span className="mr-1 shrink-0 font-semibold">To:</span>
          <input
            type="tel"
            className="min-w-0 flex-1 rounded-full border border-gray-300 px-3 py-1 text-[11px] outline-none focus:border-[#1976D2]"
            value={toOverride}
            onChange={(e) => setToOverride(e.target.value)}
            placeholder="+44 7123 456789"
          />
        </div>

        {/* CHAT BODY */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[#f5f5f5] px-3 py-3 sm:px-4">
          {loadingMessages && (
            <div className="text-center text-[11px] text-gray-700 opacity-70 mt-2">Loading SMS history…</div>
          )}

          {loadError && <div className="text-center text-[11px] text-red-700 opacity-90 mt-2 mb-2 px-3">{loadError}</div>}

          {!loadingMessages && !loadError && messages.length === 0 && (
            <div className="text-center text-[11px] text-gray-700 opacity-70 mt-2">
              SMS history for this thread will appear here once messages are sent or received.
            </div>
          )}

          {messages.map((m) => {
            const isOutbound = m.direction === "outbound";
            return (
              <div key={m.id} className={`w-full flex mb-1 ${isOutbound ? "justify-end" : "justify-start"}`}>
                <div
                  className={`relative max-w-[86%] rounded-2xl px-3 py-2 text-sm sm:max-w-[70%] ${
                    isOutbound ? "bg-[#1976D2] text-white" : "bg-[#E3F2FD] text-gray-900"
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{m.body}</div>

                  {m.created_at && (
                    <div className={`text-[10px] mt-1 text-right ${isOutbound ? "text-blue-100" : "text-gray-500"}`}>
                      {new Date(m.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* INPUT BAR */}
        <div className="flex shrink-0 flex-col gap-2 border-t bg-[#fafafa] px-3 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-4">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              className="w-full min-w-0 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm outline-none focus:border-[#1976D2]"
              placeholder="Enter text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </div>

          <div className="flex w-full flex-col gap-1 sm:w-auto sm:items-end">
            <button
              type="button"
              onClick={handleSend}
              className="w-full min-w-[88px] rounded-full bg-btnActionNormal px-5 py-2 text-sm font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionClick disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              disabled={isSending || !draft.trim()}
            >
              {isSending ? "Sending…" : "Send"}
            </button>
            <div className="text-right text-[10px] text-gray-500">{remaining} characters remaining</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SmsMessageModal;
